import type { DeviceResponseV5 } from '@emma/api_client/types';
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  CheckboxGroup,
  Flex,
  type IModalProps,
  Input,
  Modal,
  Tag,
  Text,
} from '@lego/klik-ui';
import { useMemo, useState } from 'react';
import { Loader } from '@/components/common/Loader';

type Props = {
  devices: DeviceResponseV5[];
  isLoading: boolean;
  selectedDeviceIds: number[];
  onConfirm: (deviceIds: number[]) => void;
} & Omit<IModalProps, 'children'>;

export const DevicePickerModal = ({
  devices,
  isLoading,
  selectedDeviceIds: initialSelectedIds,
  onConfirm,
  isOpen,
  onClose,
}: Props) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(() => new Set(initialSelectedIds));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) => d.name.toLowerCase().includes(q) || String(d.id).includes(q));
  }, [devices, search]);

  const handleToggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected(new Set(filtered.map((d) => d.id)));
  };

  const handleClear = () => {
    setSelected(new Set());
  };

  const handleConfirm = () => {
    onConfirm([...selected]);
    setSearch('');
    onClose();
  };

  const handleClose = () => {
    setSelected(new Set(initialSelectedIds));
    setSearch('');
    onClose();
  };

  return (
    <Modal hideIcon isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header fontSize="xl">Select Devices for Wave</Modal.Header>
        <Modal.CloseButton />
        <Modal.Body>
          <Flex direction="column" gap={3}>
            <Input
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or ID…"
              size="sm"
              value={search}
            />
            <Flex align="center" gap={2}>
              <Text color="gray.500" fontSize="sm">
                {selected.size} selected
              </Text>
              <Button onClick={handleSelectAll} size="xs" variant="ghost">
                Select all visible
              </Button>
              {selected.size > 0 && (
                <Button onClick={handleClear} size="xs" variant="ghost">
                  Clear
                </Button>
              )}
            </Flex>
            <Box maxH="360px" overflowY="auto">
              {isLoading ? (
                <Loader />
              ) : devices.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  No devices available.
                </Text>
              ) : filtered.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  No devices match your search.
                </Text>
              ) : (
                <CheckboxGroup>
                  {filtered.map((device) => (
                    <Flex align="center" gap={2} key={device.id} p={1}>
                      <Checkbox
                        isChecked={selected.has(device.id)}
                        onChange={() => handleToggle(device.id)}
                      >
                        <Text fontSize="sm">
                          {device.name}{' '}
                          <Text as="span" color="gray.500" fontFamily="mono">
                            #{device.id}
                          </Text>
                        </Text>
                      </Checkbox>
                      {device.isConnected !== undefined && (
                        <Tag colorScheme={device.isConnected ? 'green' : 'gray'} size="sm">
                          {device.isConnected ? 'Online' : 'Offline'}
                        </Tag>
                      )}
                    </Flex>
                  ))}
                </CheckboxGroup>
              )}
            </Box>
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Button onClick={handleConfirm}>
              Confirm ({selected.size} device{selected.size !== 1 ? 's' : ''})
            </Button>
            <Button onClick={handleClose} variant="outline">
              Cancel
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};
